import {
  Dispatch,
  ReactNode,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import createSafeContext from '@paradigm/utils/createSafeContext';
import {
  getUserId,
  selectIsAuthenticated,
} from '@paradigm/store/src/auth/selectors';

import {
  loadSidePanelWidth,
  persistSidePanelWidth,
  SIDE_PANEL_DEFAULT_WIDTH,
} from '#/components/Terminal/layout-constants';
import { TerminalThemeVars } from '#/components/Terminal/TerminalThemeScope';

/**
 * LaunchDarkly flag that gates the entire Block Terminal feature — both
 * the bottom-panel tab and the right-rail side-panel mount.
 */
export const FEATURE_BLOCK_TERMINAL = 'feature_block_terminal';

/**
 * Where the terminal docks when open.
 *
 *   - `bottom-tab` — embedded in the BottomPanel as one of the tab views
 *     (the historical Paradigm Block default).
 *   - `side-panel` — fixed-position vertical strip on the right edge of the
 *     workspace; doesn't share space with the bottom panel.
 */
export type PinMode = 'bottom-tab' | 'side-panel';

interface Actions {
  readonly open: () => void;
  readonly close: () => void;
  readonly toggle: () => void;
  readonly setPinMode: (mode: PinMode) => void;
  /**
   * Update the in-memory side-panel width — state only, no persistence.
   * Use this during a pointer-move drag (~60/sec) to avoid thrashing
   * localStorage with synchronous writes. Pair with `commitSidePanelWidth`
   * on pointer-up to persist the final value.
   */
  readonly setSidePanelWidth: (width: number) => void;
  /** Persist the side-panel width once a drag ends. Also updates state. */
  readonly commitSidePanelWidth: (width: number) => void;
}

interface State {
  readonly isOpen: boolean;
  readonly pinMode: PinMode;
  /**
   * Outer width (in px) of the side-panel mount. The terminal package
   * manages its own internal layout; the host only sizes the panel.
   */
  readonly sidePanelWidth: number;
}

// `pinMode` is a per-user preference (one user prefers side-panel, another
// the bottom tab) and must not leak across users on shared machines, so we
// namespace it by userId the same way the terminal's storage keys are.
// `isOpen` is transient UI state — it resets on logout and is not scoped
// by user.
const LEGACY_PIN_MODE_KEY = 'paradigm_terminal_pin_mode';
const OPEN_KEY = 'paradigm_terminal_open';

const DEFAULT_STATE: State = {
  isOpen: false,
  pinMode: 'bottom-tab',
  sidePanelWidth: SIDE_PANEL_DEFAULT_WIDTH,
};

function pinModeKey(userId: string): string {
  return `${userId.toLowerCase()}:paradigm_terminal_pin_mode`;
}

function loadBoolean(key: string, fallback: boolean): boolean {
  try {
    const v = localStorage.getItem(key);
    if (v == null) return fallback;
    return v === 'true';
  } catch {
    return fallback;
  }
}

function loadPinModeForUser(userId: string | null): PinMode {
  if (userId == null) return DEFAULT_STATE.pinMode;
  try {
    const v = localStorage.getItem(pinModeKey(userId));
    if (v != null) return v === 'side-panel' ? 'side-panel' : 'bottom-tab';
    // One-shot migration from the legacy unscoped key to the per-user key,
    // so users who set their pin mode before this change don't lose it.
    const legacy = localStorage.getItem(LEGACY_PIN_MODE_KEY);
    if (legacy != null) {
      try {
        localStorage.setItem(pinModeKey(userId), legacy);
        localStorage.removeItem(LEGACY_PIN_MODE_KEY);
      } catch {
        // best-effort migration
      }
      return legacy === 'side-panel' ? 'side-panel' : 'bottom-tab';
    }
    return DEFAULT_STATE.pinMode;
  } catch {
    return DEFAULT_STATE.pinMode;
  }
}

function loadInitialState(userIdStr: string | null): State {
  if (userIdStr == null) return DEFAULT_STATE;
  return {
    isOpen: loadBoolean(OPEN_KEY, false),
    pinMode: loadPinModeForUser(userIdStr),
    sidePanelWidth: loadSidePanelWidth(userIdStr),
  };
}

export const [useTerminalDrawerState, StateProvider] = createSafeContext<State>(
  'TerminalDrawerState',
);

export const [useTerminalDrawerActions, ActionsProvider] =
  createSafeContext<Actions>('TerminalDrawerActions');

interface Props {
  readonly children: ReactNode;
}

export function TerminalDrawerProvider({ children }: Props) {
  const userId = useSelector(getUserId);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  // The auth reducer resets `user.id` to `0` (not `null`) on
  // `logoutSuccess`, so treat the falsy / "empty user" case the same as
  // logged-out. We *also* require `isAuthenticated` because the
  // session-reuse saga can race: it dispatches `loginSuccess` (restoring
  // `user.id`) before React renders the intermediate `user.id === 0`
  // state, so a `userId`-only check sees no change and the transient
  // wipe never fires — which left the side panel auto-open across a
  // logout/login cycle.
  const userIdStr =
    isAuthenticated &&
    userId != null &&
    userId !== 0 &&
    String(userId).length > 0
      ? String(userId)
      : null;

  const [state, setState] = useState<State>(() => loadInitialState(userIdStr));

  // Mirror current userId for actions to read at call time without
  // re-creating the actions object on every login state change.
  const userIdRefForActions = useRef<string | null>(userIdStr);
  userIdRefForActions.current = userIdStr;

  const actions = useMemo(
    () => createActions(setState, userIdRefForActions),
    [],
  );

  // Force-close the side panel on every auth boundary (login *and*
  // logout). `isOpen` is transient; persisting it across an auth flip is
  // what causes `BlockTerminalSidePanel` to mount `BlockTerminalTab` →
  // the terminal's WebSocket connect immediately on re-login, which —
  // combined with a stale localStorage agent URL — fans out into the
  // socket-pool / Sentry storm tracked in #34133. Pin mode in
  // localStorage (per-user) survives; only the in-memory `isOpen` is
  // reset.
  //
  // We key the boundary off *both* `isAuthenticated` and `userId`: a
  // same-user logout/login may flip `isAuthenticated`, but a batched
  // cross-user switch (user A → user B in a single committed render)
  // would leave `isAuthenticated` at `true` both before and after — only
  // `userId` changes — so an `[isAuthenticated]`-only dep would miss it
  // and the previous user's open drawer would leak to the next user.
  const prevAuthRef = useRef<boolean>(isAuthenticated);
  const prevUserIdRef = useRef<number>(userId);
  useEffect(() => {
    const authChanged = prevAuthRef.current !== isAuthenticated;
    const userChanged = prevUserIdRef.current !== userId;
    if (!authChanged && !userChanged) return;
    const becomingLoggedOut = prevAuthRef.current && !isAuthenticated;
    const switchingUser =
      isAuthenticated &&
      userId !== 0 &&
      prevUserIdRef.current !== 0 &&
      prevUserIdRef.current !== userId;
    prevAuthRef.current = isAuthenticated;
    prevUserIdRef.current = userId;
    setState((prev) => ({
      ...prev,
      isOpen: false,
      // Drop the previous user's pinMode from in-memory state on a
      // boundary that hands the device to a different user (or to no
      // user) so the sign-in screen / next user doesn't briefly see the
      // previous user's preference. localStorage keeps the per-user
      // value and the login hydration effect restores it.
      ...(becomingLoggedOut || switchingUser
        ? { pinMode: DEFAULT_STATE.pinMode }
        : {}),
    }));
    try {
      localStorage.removeItem(OPEN_KEY);
    } catch {
      // storage unavailable — ignore
    }
  }, [isAuthenticated, userId]);

  // Hydrate per-user pinMode + panel width on login (so a user's
  // preferred layout survives). `isOpen` is reset by the auth-boundary
  // effect above and is not re-hydrated from storage.
  //
  // This MUST be a layout effect, not a passive one: `userId` arrives
  // asynchronously (the session-reuse saga dispatches `loginSuccess` a
  // tick after first render), so the first post-login render still holds
  // the `DEFAULT_STATE` width/pinMode from `loadInitialState(null)`. A
  // passive `useEffect` would let the browser paint that wrong-width frame
  // first; `useLayoutEffect` applies the persisted values before paint,
  // so the intermediate frame is never shown.
  const hydratedForRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (userIdStr == null) {
      hydratedForRef.current = null;
      return;
    }
    if (hydratedForRef.current === userIdStr) return;
    // Read localStorage now — not inside the setState callback. The
    // persist-pinMode effect below runs after this one in the same
    // commit phase and writes the *previous* render's `state.pinMode`
    // back to localStorage; a deferred read would pick up that stale
    // value instead of the just-signed-in user's stored preference.
    const persistedPinMode = loadPinModeForUser(userIdStr);
    const persistedSidePanelWidth = loadSidePanelWidth(userIdStr);
    setState((prev) => ({
      ...prev,
      pinMode: persistedPinMode,
      sidePanelWidth: persistedSidePanelWidth,
    }));
    hydratedForRef.current = userIdStr;
  }, [userIdStr]);

  // Skip persistence while unauthenticated so the logout wipe above isn't
  // immediately re-created by the effects writing default values back.
  useEffect(() => {
    if (userIdStr == null) return;
    try {
      localStorage.setItem(OPEN_KEY, String(state.isOpen));
    } catch {
      // storage unavailable (private mode) — ignore
    }
  }, [userIdStr, state.isOpen]);

  useEffect(() => {
    if (userIdStr == null) return;
    try {
      localStorage.setItem(pinModeKey(userIdStr), state.pinMode);
    } catch {
      // ignore
    }
  }, [userIdStr, state.pinMode]);

  // Toggle drawer with ` (backtick/tilde). Cmd/Ctrl+` works from anywhere
  // including inputs, so users can close while typing in chat. Plain `
  // skips editable targets to avoid hijacking literal backticks.
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key !== '`' && e.key !== '~') return;
      if (e.altKey) return;
      const withModifier = e.metaKey || e.ctrlKey;
      if (!withModifier) {
        const target = e.target as HTMLElement | null;
        const tag = target?.tagName;
        const isEditable =
          tag === 'INPUT' ||
          tag === 'TEXTAREA' ||
          tag === 'SELECT' ||
          target?.isContentEditable === true;
        if (isEditable) return;
      }
      e.preventDefault();
      actions.toggle();
    }
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [actions]);

  return (
    <StateProvider value={state}>
      <ActionsProvider value={actions}>
        {/* Terminal theme variables on :root — must be global so popups the
            terminal portals to document.body (account menu, model picker)
            inherit them. Renders null; injects into <head>. */}
        <TerminalThemeVars />
        {children}
      </ActionsProvider>
    </StateProvider>
  );
}

function createActions(
  setState: Dispatch<SetStateAction<State>>,
  userIdRef: { current: string | null },
): Actions {
  return {
    open() {
      setState((prev) => ({ ...prev, isOpen: true }));
    },
    close() {
      setState((prev) => ({ ...prev, isOpen: false }));
    },
    toggle() {
      setState((prev) => ({ ...prev, isOpen: !prev.isOpen }));
    },
    setPinMode(mode: PinMode) {
      setState((prev) => ({ ...prev, pinMode: mode }));
    },
    setSidePanelWidth(width: number) {
      setState((prev) => ({ ...prev, sidePanelWidth: width }));
    },
    commitSidePanelWidth(width: number) {
      setState((prev) => ({ ...prev, sidePanelWidth: width }));
      const id = userIdRef.current;
      if (id != null) persistSidePanelWidth(id, width);
    },
  };
}
